import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./DocumentVerification.scss";
import Logo from "../../iamges/logo.png";
import { clearSession, getSession, initials } from "./auth";
import {
  DocumentVerificationResponse,
  submitDocumentVerification,
  fetchDocumentVerification,
} from "../../services/documentVerification.service";

// ---------------------------------------------------------------------------
// Data — mirrors the "Document Verification" design.
// ---------------------------------------------------------------------------

const PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick",
  "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
  "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon",
];

type SlotKey = "id" | "application" | "income" | "credit" | "bank" | "letter";

interface FileEntry { name: string; size: string; file?: File; }

interface Slot {
  key: SlotKey;
  tier: 0 | 1 | 2;
  label: string;
  hint: string;
  icon: string;
  multi?: boolean;
}

const SLOTS: Slot[] = [
  { key: "id", tier: 0, label: "Photo ID", hint: "Government-issued — licence, passport, or provincial ID", icon: "ph-identification-card" },
  { key: "application", tier: 0, label: "Rental application", hint: "Holds the stated income, employer, and rental history — the anchor for every cross-check", icon: "ph-file-text" },
  { key: "income", tier: 0, label: "Income document", hint: "At least one of: pay stub, T4, or Notice of Assessment", icon: "ph-receipt", multi: true },
  { key: "credit", tier: 1, label: "Credit report from the tenant", hint: "The applicant can share a report they pulled themselves. Adding it makes the verification more complete.", icon: "ph-chart-line-up" },
  { key: "bank", tier: 1, label: "Bank statements", hint: "Last 3 months. These corroborate the income stated on the application.", icon: "ph-bank", multi: true },
  { key: "letter", tier: 2, label: "Employment letter", hint: "From the employer, confirming role and salary", icon: "ph-envelope-simple" },
];

const TIER_META = [
  { title: "Required", badge: "Required", badgeBg: "#FFEFD9", badgeFg: "#8A4B10", note: "We cannot run the cross-check without these." },
  { title: "Highly recommended", badge: "Recommended", badgeBg: "var(--dv-sage-100)", badgeFg: "var(--dv-sage-700)", note: "Never blocking — they just make the summary more complete." },
  { title: "Optional", badge: "Optional", badgeBg: "var(--dv-gray-100)", badgeFg: "var(--dv-gray-600)", note: "Add it if the tenant provided one." },
];

interface ReportRow {
  label: string;
  detail?: string;
  value?: string;
  status?: string;
  badgeColor?: "success" | "neutral" | "warning";
  badgeIcon?: string;
  noBadge?: boolean;
}

const OK = { badgeColor: "success" as const, badgeIcon: "ph-check-circle" };
const NEUTRAL = { badgeColor: "neutral" as const, badgeIcon: "ph-minus-circle" };
const FIG = { noBadge: true };

interface ReportSection {
  icon: string;
  title: string;
  sub: string;
  span?: string;
  rows: ReportRow[];
  caveat?: string;
}

const STEP_NAMES = ["Consent", "Upload documents", "Check", "Summary"];

const toArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const asText = (value: any, fallback = ""): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
};

const statusBadge = (status?: string): Pick<ReportRow, "badgeColor" | "badgeIcon"> => {
  const normalized = String(status || "").toLowerCase();
  if (
    normalized.includes("pass") ||
    normalized.includes("consistent") ||
    normalized.includes("verified") ||
    normalized.includes("read") ||
    normalized.includes("no_signals")
  ) {
    return OK;
  }
  if (
    normalized.includes("fail") ||
    normalized.includes("mismatch") ||
    normalized.includes("concern") ||
    normalized.includes("failed")
  ) {
    return { badgeColor: "warning", badgeIcon: "ph-warning-circle" };
  }
  return NEUTRAL;
};

const listRows = (items: any[], emptyLabel: string): ReportRow[] => {
  const rows = items
    .map((item) => {
      if (typeof item === "string") return { label: item, ...FIG };
      if (!item || typeof item !== "object") return null;
      return {
        label:
          asText(item.label) ||
          asText(item.finding) ||
          asText(item.risk) ||
          asText(item.pattern) ||
          asText(item.question) ||
          asText(item.document) ||
          "Finding",
        detail:
          asText(item.detail) ||
          asText(item.evidence) ||
          asText(item.reason) ||
          toArray(item.findings).join(" · ") ||
          undefined,
        value:
          asText(item.value) ||
          asText(item.verifiedNetIncomeMonthly) ||
          asText(item.totalDeposits) ||
          undefined,
        status: asText(item.status) || asText(item.severity) || undefined,
        ...(item.status || item.severity ? statusBadge(item.status || item.severity) : FIG),
      } as ReportRow;
    })
    .filter(Boolean) as ReportRow[];

  return rows.length ? rows : [{ label: emptyLabel, status: "Not provided", ...NEUTRAL }];
};

const tamperRiskBadge = (risk?: string): Pick<ReportRow, "badgeColor" | "badgeIcon"> => {
  const normalized = String(risk || "").toLowerCase();
  if (normalized === "high" || normalized === "medium") {
    return { badgeColor: "warning", badgeIcon: "ph-warning-circle" };
  }
  if (normalized === "low") {
    return OK;
  }
  return NEUTRAL;
};

const buildTamperRows = (tamper: any): ReportRow[] => {
  if (!tamper) {
    return [{ label: "AI tamper screening was not returned.", status: "Missing", ...NEUTRAL }];
  }

  const rows: ReportRow[] = [
    {
      label: "Overall tamper risk",
      detail: asText(tamper.summary, "AI tamper screening completed."),
      value:
        tamper.tamperingScore !== undefined && tamper.tamperingScore !== null
          ? `${tamper.tamperingScore}/100`
          : undefined,
      status: asText(tamper.overallRisk, "Inconclusive"),
      ...tamperRiskBadge(tamper.overallRisk),
    },
  ];

  const findings = toArray(tamper.findings);
  if (findings.length) {
    findings.forEach((finding) => {
      rows.push({
        label: `${asText(finding.document, "Document")}${finding.page ? ` · page ${finding.page}` : ""}`,
        detail:
          asText(finding.finding) ||
          asText(finding.evidence) ||
          "Potential tampering signal needs review.",
        value: finding.confidence !== undefined ? `${Math.round(Number(finding.confidence) * 100)}%` : undefined,
        status: asText(finding.severity, "Review"),
        ...tamperRiskBadge(finding.severity),
      });
      if (finding.evidence || finding.recommendedReview) {
        rows.push({
          label: asText(finding.evidence, "Manual review required"),
          detail: asText(finding.recommendedReview),
          ...FIG,
        });
      }
    });
  } else {
    rows.push({
      label: "No obvious tampering signals found",
      detail:
        "This does not prove authenticity. It only means the AI did not find visible tamper indicators in readable files.",
      status: "Low",
      ...OK,
    });
  }

  return rows;
};

const buildReportSections = (aiResult: any): ReportSection[] => {
  if (!aiResult || aiResult.skipped) {
    return [
      {
        icon: "ph-info",
        title: "Verification result",
        sub: "No AI result yet",
        span: "span 2",
        rows: [
          {
            label: aiResult?.error || "The document verification result has not been generated yet.",
            status: "Pending",
            ...NEUTRAL,
          },
        ],
      },
    ];
  }

  return [
    {
      icon: "ph-files",
      title: "Documents reviewed",
      sub: "Section 1",
      span: "span 2",
      rows: listRows(
        toArray(aiResult.documentLoadReport).map((item) => ({
          label: item.name || item.section || "Document",
          detail: item.reason || item.section,
          status: item.status,
        })),
        "No document load report returned by AI."
      ),
    },
    {
      icon: "ph-shield-warning",
      title: "AI tamper screening",
      sub: "Visual, formatting, arithmetic, and consistency indicators",
      span: "span 2",
      rows: buildTamperRows(aiResult.tamperScreening),
      caveat:
        aiResult.tamperScreening?.disclaimer ||
        "AI tamper screening identifies suspicious indicators only. It is not proof that a document is authentic or fake.",
    },
    {
      icon: "ph-user-focus",
      title: "Identity consistency",
      sub: "Section 2",
      rows: [
        {
          label: asText(aiResult.identity?.findings?.[0]) || "Identity review",
          detail: toArray(aiResult.identity?.redFlags).join(" · ") || undefined,
          status: asText(aiResult.identity?.status, "Inconclusive"),
          ...statusBadge(aiResult.identity?.status),
        },
        ...listRows(toArray(aiResult.identity?.findings).slice(1), "No additional identity findings."),
      ],
    },
    {
      icon: "ph-money",
      title: "Income verification",
      sub: "Section 3",
      rows: listRows(aiResult.incomeVerification || aiResult.incomeAsStated || [], "No income verification returned."),
    },
    {
      icon: "ph-bank",
      title: "Financial summary",
      sub: "Section 4",
      span: "span 2",
      rows: listRows(aiResult.perPersonFinancials || aiResult.financialSummary || [], "No financial summary returned."),
    },
    {
      icon: "ph-percent",
      title: "Income relative to rent",
      sub: "Section 5",
      span: "span 2",
      rows: [
        ...listRows(toArray(aiResult.affordability?.individualRentToIncome), "No rent-to-income breakdown returned."),
        {
          label: "Combined verified rent share",
          value: asText(aiResult.affordability?.combinedVerifiedRentShare, "Not calculated"),
          ...FIG,
        },
        {
          label: "Who is carrying rent",
          detail: asText(aiResult.affordability?.carriedBy, "Not determined"),
          ...FIG,
        },
      ],
      caveat:
        "Shown as factual figures from uploaded documents. HomeOwner does not apply an approval threshold.",
    },
    {
      icon: "ph-arrows-left-right",
      title: "Cross-document consistency",
      sub: "Section 6",
      rows: [
        {
          label: "Overall consistency",
          detail: toArray(aiResult.crossDocumentConsistency?.omissions).join(" · ") || undefined,
          status: asText(aiResult.crossDocumentConsistency?.status, "Inconclusive"),
          ...statusBadge(aiResult.crossDocumentConsistency?.status),
        },
        ...listRows(toArray(aiResult.crossDocumentConsistency?.findings), "No consistency findings returned."),
      ],
    },
    {
      icon: "ph-file-magnifying-glass",
      title: "Document integrity",
      sub: "Section 7",
      rows: listRows(aiResult.documentIntegrity || [], "No document integrity findings returned."),
      caveat:
        "No tampering signals found is not proof of authenticity. This is AI-assisted screening, not forensic verification.",
    },
    {
      icon: "ph-chart-line-up",
      title: "Tenant-provided credit score",
      sub: "Section 8",
      span: "span 2",
      rows: aiResult.tenantProvidedCreditScore
        ? [
            {
              label: "Score on the report the applicant provided",
              detail:
                asText(aiResult.tenantProvidedCreditScore.reportDate) ||
                asText(aiResult.tenantProvidedCreditScore.note),
              value: asText(aiResult.tenantProvidedCreditScore.score, "Not provided"),
              status: "Self-provided",
              ...NEUTRAL,
            },
            ...listRows(toArray(aiResult.tenantProvidedCreditScore.debtsListed), "No listed debts returned."),
          ]
        : [{ label: "No tenant-provided credit score returned.", status: "Not provided", ...NEUTRAL }],
    },
    {
      icon: "ph-warning-circle",
      title: "Risks and next steps",
      sub: "Bottom line",
      span: "span 2",
      rows: [
        ...listRows(aiResult.riskRanking || [], "No risk ranking returned."),
        ...listRows(aiResult.questionsForPooja || [], "No questions returned."),
        ...listRows(aiResult.furtherDocumentsToRequest || [], "No further document requests returned."),
      ],
    },
  ];
};

const buildSummaryColumns = (aiResult: any) => {
  if (!aiResult || aiResult.skipped) {
    return [
      {
        title: "Status",
        icon: "ph-circle-notch",
        bullet: "ph-dot-outline",
        color: "var(--dv-gray-500)",
        items: [aiResult?.error || "Waiting for verification result."],
      },
    ];
  }

  return [
    {
      title: "What we set out to verify",
      icon: "ph-target",
      bullet: "ph-dot-outline",
      color: "var(--dv-gray-500)",
      items: [
        "Identity consistency across the documents",
        "Income against bank deposits",
        "Cross-document consistency",
        "Document integrity signals",
        "Affordability from verified information",
      ],
    },
    {
      title: "Verified clearly",
      icon: "ph-check-circle",
      bullet: "ph-check",
      color: "var(--dv-success-600)",
      items: toArray(aiResult.verifiedClearly).length
        ? toArray(aiResult.verifiedClearly).map(String)
        : [aiResult.summary || "Verification completed."],
    },
    {
      title: "Flagged or could not verify",
      icon: "ph-warning-circle",
      bullet: "ph-minus",
      color: "var(--dv-warning-700)",
      items: [
        ...toArray(aiResult.flaggedOrCouldNotVerify).map(String),
        ...toArray(aiResult.inconsistencies).map(String),
        ...toArray(aiResult.missingDocuments).map((item) => `Missing: ${item}`),
      ].filter(Boolean).length
        ? [
            ...toArray(aiResult.flaggedOrCouldNotVerify).map(String),
            ...toArray(aiResult.inconsistencies).map(String),
            ...toArray(aiResult.missingDocuments).map((item) => `Missing: ${item}`),
          ]
        : ["No flagged items returned by AI."],
    },
  ];
};

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const FeaturedIcon: React.FC<{ icon: string; size?: number }> = ({ icon, size = 40 }) => (
  <div className="dv-featured-icon" style={{ width: size, height: size }}>
    <i className={`ph ${icon}`} style={{ fontSize: Math.round(size * 0.46) }} />
  </div>
);

const Badge: React.FC<{ color: "success" | "neutral" | "warning"; icon?: string; children: React.ReactNode }> = ({ color, icon, children }) => (
  <span className={`dv-badge dv-badge--${color}`}>
    {icon && <i className={`ph ${icon}`} />}
    {children}
  </span>
);

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface SlotCardProps {
  slot: Slot;
  entries: FileEntry[];
  onAddFiles: (slot: Slot, files: FileList | File[]) => void;
  onRemoveFile: (slot: Slot, index: number) => void;
}

const SlotCard: React.FC<SlotCardProps> = ({ slot, entries, onAddFiles, onRemoveFile }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const filled = entries.length > 0;
  const maxReached = slot.multi ? entries.length >= 10 : false;

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onAddFiles(slot, e.dataTransfer.files);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onAddFiles(slot, e.target.files);
    }
    e.target.value = "";
  };

  return (
    <div
      className={`dv-slot${filled ? " dv-slot--filled" : ""}${isDragOver ? " dv-slot--drag-over" : ""}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={!!slot.multi}
        accept=".pdf,.png,.jpg,.jpeg,image/*"
        style={{ display: "none" }}
        onChange={handleInputChange}
      />

      <div className="dv-slot__head">
        <div className="dv-slot__icon" style={{ background: filled ? "var(--dv-success-50)" : "var(--dv-orange-50)" }}>
          <i className={`ph ${slot.icon}`} style={{ fontSize: 20, color: filled ? "var(--dv-success-600)" : "var(--dv-orange-500)" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <div className="dv-slot__label">
            <span>{slot.label}</span>
            {filled && <i className="ph ph-check-circle" style={{ fontSize: 17, color: "var(--dv-success-500)" }} />}
          </div>
          <span className="dv-slot__hint">{slot.hint}</span>
        </div>
      </div>

      {entries.map((f, i) => {
        const isImg = /\.(png|jpe?g|webp|gif|svg)$/i.test(f.name);
        return (
          <div className="dv-file-row" key={`${f.name}-${i}`}>
            <i className={`ph ${isImg ? "ph-file-image" : "ph-file-pdf"}`} />
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
              <span className="dv-file-row__name" title={f.name}>{f.name}</span>
              <span className="dv-file-row__size">{f.size}</span>
            </div>
            <button
              type="button"
              className="dv-file-row__remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFile(slot, i);
              }}
              title="Remove file"
            >
              <i className="ph ph-trash" style={{ fontSize: 15 }} />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className={`dv-drop${isDragOver ? " dv-drop--active" : ""}`}
        disabled={maxReached}
        onClick={handleButtonClick}
      >
        <i className={`ph ${maxReached ? "ph-check" : filled && !slot.multi ? "ph-arrows-clockwise" : filled ? "ph-plus" : "ph-upload-simple"}`} />
        {maxReached
          ? "All files added"
          : filled && !slot.multi
          ? "Change file"
          : filled
          ? "Add another file"
          : "Drag a file here, or click to upload"}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const DocumentVerification: React.FC = () => {
  const navigate = useNavigate();
  const session = getSession();
  const displayName = session?.fullName || session?.email || "Account";

  const handleLogout = () => {
    clearSession();
    navigate("/document-verification/login", { replace: true });
  };

  const [screen, setScreen] = useState<1 | 2 | 3 | 4>(1);
  const [province, setProvince] = useState("");
  const [consent, setConsent] = useState(false);
  const [applicant, setApplicant] = useState("");
  const [files, setFiles] = useState<Partial<Record<SlotKey, FileEntry[]>>>({});
  const [stage, setStage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [verificationResult, setVerificationResult] =
    useState<DocumentVerificationResponse["data"] | null>(null);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const vId = params.get("verificationId");
    const shouldPrint = params.get("print") === "true";
    let cancelled = false;

    if (vId) {
      setIsLoadingExisting(true);
      setSubmitError("");
      fetchDocumentVerification(vId)
        .then((record) => {
          if (cancelled) return;
          if (record) {
            setVerificationResult(record as any);
            setApplicant(record.applicantName || "");
            setProvince(record.province || "");
            setConsent(true);
            setScreen(4);
            if (shouldPrint) {
              setTimeout(() => {
                window.print();
              }, 700);
            }
          }
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("Failed to load verification record", err);
          setSubmitError(err?.message || "Could not load this document verification report.");
        })
        .finally(() => {
          if (!cancelled) setIsLoadingExisting(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const isQuebec = province === "Quebec";
  const canContinue = !!province && !isQuebec && consent;
  const showConsent = !!province && !isQuebec;

  const has = (k: SlotKey) => (files[k] || []).length > 0;
  const requiredMet = has("id") && has("application") && has("income");
  const recMissing = [!has("credit") && "a credit report", !has("bank") && "bank statements"].filter(Boolean) as string[];
  const fileCount = Object.values(files).reduce((n, a) => n + (a ? a.length : 0), 0);

  const handleAddFiles = (slot: Slot, newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    if (!fileArray.length) return;

    const entriesToAdd: FileEntry[] = fileArray.map((f) => ({
      name: f.name,
      size: formatFileSize(f.size),
      file: f,
    }));

    setFiles((prev) => {
      const cur = prev[slot.key] || [];
      if (!slot.multi) {
        return { ...prev, [slot.key]: [entriesToAdd[0]] };
      } else {
        const existingKeys = new Set(cur.map((c) => `${c.name}_${c.size}`));
        const unique = entriesToAdd.filter((e) => !existingKeys.has(`${e.name}_${e.size}`));
        return { ...prev, [slot.key]: [...cur, ...(unique.length > 0 ? unique : entriesToAdd)] };
      }
    });
  };

  const removeFile = (slot: Slot, i: number) => {
    const cur = (files[slot.key] || []).filter((_, j) => j !== i);
    setFiles({ ...files, [slot.key]: cur });
  };

  const goStart = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setScreen(1);
    setFiles({});
    setConsent(false);
    setProvince("");
    setStage(0);
    setApplicant("");
    setSubmitError("");
    setVerificationResult(null);
    setIsSubmitting(false);
  };

  const runVerification = async () => {
    if (!requiredMet) return;
    timers.current.forEach(clearTimeout);
    setStage(0);
    setScreen(3);
    setSubmitError("");
    setVerificationResult(null);
    setIsSubmitting(true);

    timers.current = [setTimeout(() => setStage(1), 1800)];

    const photoId = files.id?.[0]?.file;
    const rentalApplication = files.application?.[0]?.file;
    const incomeDocuments = (files.income || []).map((entry) => entry.file).filter(Boolean) as File[];
    const bankStatements = (files.bank || []).map((entry) => entry.file).filter(Boolean) as File[];

    if (!photoId || !rentalApplication || incomeDocuments.length === 0) {
      setSubmitError("Please upload the required documents before verification.");
      setIsSubmitting(false);
      setScreen(2);
      return;
    }

    try {
      const response = await submitDocumentVerification({
        verificationId: `dv-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        province,
        applicantName: applicant.trim() || displayName,
        photoId,
        rentalApplication,
        incomeDocuments,
        creditReport: files.credit?.[0]?.file || null,
        bankStatements,
        employmentLetter: files.letter?.[0]?.file || null,
      });

      setStage(2);
      setVerificationResult(response.data);
      if (response.data.aiResult || response.data.aiError) {
        setScreen(4);
      } else {
        setScreen(3);
      }
    } catch (error: any) {
      setSubmitError(error?.message || "Document verification failed. Please try again.");
      setScreen(2);
    } finally {
      setIsSubmitting(false);
    }
  };

  const applicantLabel =
    verificationResult?.applicantName || applicant.trim() || displayName;
  const reportGenerated = verificationResult?.aiProcessedAt || verificationResult?.createdAt;
  const currentAiResult = useMemo(
    () =>
      verificationResult?.aiResult ||
      (verificationResult?.aiError
        ? { skipped: true, error: verificationResult.aiError }
        : null),
    [verificationResult?.aiError, verificationResult?.aiResult]
  );
  const uploadQueued =
    !!verificationResult &&
    !verificationResult.aiResult &&
    !verificationResult.aiError &&
    ["uploaded", "checking"].includes(verificationResult.status);
  const reportSummaryCols = useMemo(
    () => buildSummaryColumns(currentAiResult),
    [currentAiResult]
  );
  const reportSections = useMemo(
    () => buildReportSections(currentAiResult),
    [currentAiResult]
  );

  const stages = [
    {
      label: "Checking documents for signs of tampering",
      sub: "Metadata, structure, and internal figures",
    },
    {
      label: "Cross-checking information across documents",
      sub: "Name, address, employer, and income against the application",
    },
  ].map((g, i) => {
    const done = stage > i;
    const active = stage === i;
    return {
      ...g,
      icon: done ? "ph-check-circle" : active ? "ph-circle-notch" : "ph-circle",
      status: done ? "Done" : active ? "In progress" : "Waiting",
      iconColor: done
        ? "var(--dv-success-500)"
        : active
        ? "var(--dv-orange-500)"
        : "var(--dv-gray-300)",
      textColor: done || active ? "var(--dv-ink)" : "var(--dv-gray-400)",
      bg: active ? "var(--dv-orange-50)" : "var(--dv-gray-50)",
      border: active ? "var(--dv-border-brand-soft)" : "var(--dv-gray-200)",
    };
  });

  const dynamicReviewed = useMemo(() => {
    const counts: Record<SlotKey, number> = {
      id: (files.id || []).length,
      application: (files.application || []).length,
      income: (files.income || []).length,
      bank: (files.bank || []).length,
      credit: (files.credit || []).length,
      letter: (files.letter || []).length,
    };

    const list = [
      counts.id > 0 && { icon: "ph-identification-card", label: `Photo ID (${counts.id})` },
      counts.application > 0 && { icon: "ph-file-text", label: `Rental application (${counts.application})` },
      counts.income > 0 && { icon: "ph-receipt", label: `Income documents (${counts.income})` },
      counts.bank > 0 && { icon: "ph-bank", label: `Bank statements (${counts.bank})` },
      counts.credit > 0 && { icon: "ph-chart-line-up", label: `Credit report (${counts.credit})` },
      counts.letter > 0 && { icon: "ph-envelope-simple", label: `Employment letter (${counts.letter})` },
    ].filter(Boolean) as { icon: string; label: string }[];

    if (verificationResult?.documents) {
      const docs = verificationResult.documents;
      return [
        docs.photoId && { icon: "ph-identification-card", label: `Photo ID (1)` },
        docs.rentalApplication && { icon: "ph-file-text", label: `Rental application (1)` },
        docs.incomeDocuments?.length && { icon: "ph-receipt", label: `Income documents (${docs.incomeDocuments.length})` },
        docs.bankStatements?.length && { icon: "ph-bank", label: `Bank statements (${docs.bankStatements.length})` },
        docs.creditReport && { icon: "ph-chart-line-up", label: `Credit report (1)` },
        docs.employmentLetter && { icon: "ph-envelope-simple", label: `Employment letter (1)` },
      ].filter(Boolean) as { icon: string; label: string }[];
    }

    return list.length > 0 ? list : [];
  }, [files, verificationResult]);

  return (
    <div className="dv-root">
      <header className="dv-header">
        <div className="dv-brand">
          <img src={Logo} alt="HomeOwner" />
          <div>
            <div className="dv-brand__title">HomeOwner</div>
            <div className="dv-brand__sub">Document verification</div>
          </div>
        </div>
        <nav className="dv-nav">
          <button
            type="button"
            className="dv-nav-pill dv-nav-pill--active"
            onClick={() => {
              if (screen === 4) goStart();
            }}
          >
            <i className="ph ph-shield-check" />New verification
          </button>
          <button
            type="button"
            className="dv-nav-pill"
            onClick={() => navigate("/document-verification/past")}
          >
            <i className="ph ph-clock-counter-clockwise" />Past verifications
          </button>
        </nav>
        <div className="dv-user">
          <div className="dv-avatar">{initials(displayName)}</div>
          <span className="dv-user__name" style={{ fontWeight: 600, fontSize: 13 }}>{displayName}</span>
        </div>
        <button type="button" className="dv-logout-btn" onClick={handleLogout}>
          <i className="ph ph-sign-out" />
          <span className="dv-logout-btn__label">Log out</span>
        </button>
      </header>

      <div className="dv-body">
        <div className="dv-stepper">
          {STEP_NAMES.map((label, i) => {
            const n = i + 1, done = screen > n, active = screen === n;
            const bg = done ? "var(--dv-success-50)" : active ? "var(--dv-orange-500)" : "var(--dv-white)";
            const fg = done ? "var(--dv-success-700)" : active ? "#FFFFFF" : "var(--dv-gray-400)";
            const ring = done ? "var(--dv-success-500)" : active ? "var(--dv-orange-500)" : "var(--dv-gray-300)";
            const labelColor = active ? "var(--dv-ink)" : done ? "var(--dv-gray-600)" : "var(--dv-gray-400)";
            return (
              <div className="dv-step" key={label}>
                <div className="dv-step__num" style={{ background: bg, color: fg, boxShadow: `inset 0 0 0 1px ${ring}` }}>{n}</div>
                <span className="dv-step__label" style={{ color: labelColor }}>{label}</span>
                <div className="dv-step__line" />
              </div>
            );
          })}
        </div>

        {screen === 1 && (
          <div className="dv-grid-2 dv-fade">
            <div className="dv-card dv-start-card">
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <FeaturedIcon icon="ph-shield-check" size={52} />
                <h1 className="dv-h1">Verify tenant documents</h1>
                <p className="dv-lede">Upload the documents your prospective tenant provided. We check them for signs of tampering and for consistency across documents, and give you an organized summary. We do not pull a credit report, and we do not decide for you.</p>
              </div>

              <div className="dv-divider" />

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label className="dv-field-label">Which province is the rental property in?</label>
                  <span className="dv-field-hint">Rules and protected grounds differ by province, so we record this on the report.</span>
                </div>
                <div className="dv-province-list">
                  {PROVINCES.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className={`dv-pill${province === name ? " dv-pill--selected" : ""}`}
                      onClick={() => setProvince(name)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              {isQuebec && (
                <div className="dv-quebec-gate dv-fade">
                  <i className="ph ph-info dv-quebec-gate__icon" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span className="dv-quebec-gate__title">Document verification is not yet available for Quebec properties</span>
                    <p className="dv-quebec-gate__body">Quebec's privacy regime (Law 25) is stricter about this kind of document processing, so we have limited the feature to properties outside Quebec while we confirm a compliant approach. Everything else in HomeOwner works as usual for your Quebec property, and we will let you know when verification is available.</p>
                    <button type="button" className="dv-quebec-gate__link">Read how we handle your documents</button>
                  </div>
                </div>
              )}

              {showConsent && (
                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  <div className="dv-divider" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <label className="dv-field-label">Applicant name</label>
                      <span className="dv-field-hint">Used only as a label on the report. We do not verify anything against it.</span>
                    </div>
                    <input
                      className="dv-input"
                      value={applicant}
                      onChange={(e) => setApplicant(e.target.value)}
                      placeholder="Enter applicant name"
                    />
                  </div>
                  <div className="dv-divider" />
                  <div className={`dv-consent-box${consent ? " dv-consent-box--checked" : ""}`}>
                    <div
                      className={`dv-checkbox${consent ? " dv-checkbox--checked" : ""}`}
                      onClick={() => setConsent(!consent)}
                      role="checkbox"
                      aria-checked={consent}
                      tabIndex={0}
                    >
                      <i className="ph ph-check" />
                    </div>
                    <div className="dv-consent-box__text" onClick={() => setConsent(!consent)}>
                      <span className="dv-consent-box__label">I confirm these documents were provided to me by the prospective tenant, and that I have their permission to upload them for verification.</span>
                      <span className="dv-consent-box__meta">Required. Wording pending legal review.</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <button
                      type="button"
                      className={`dv-btn dv-btn--lg${canContinue ? " dv-btn--glow" : ""}`}
                      disabled={!canContinue}
                      onClick={() => canContinue && setScreen(2)}
                    >
                      Continue
                    </button>
                    <span className="dv-field-hint">{canContinue ? "Next: upload the documents." : "Tick the confirmation to continue."}</span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="dv-card" style={{ padding: 22, display: "flex", flexDirection: "column", gap: 16 }}>
                <span className="dv-info-title">What this tool does</span>
                <div className="dv-info-row">
                  <i className="ph ph-magnifying-glass" />
                  <div>
                    <div className="dv-info-row__title">Checks for signs of tampering</div>
                    <div className="dv-info-row__body">Metadata, structure, and figures that do not add up.</div>
                  </div>
                </div>
                <div className="dv-info-row">
                  <i className="ph ph-arrows-left-right" />
                  <div>
                    <div className="dv-info-row__title">Cross-checks the documents</div>
                    <div className="dv-info-row__body">Name, address, employer, and income compared against the application.</div>
                  </div>
                </div>
                <div className="dv-divider" />
                <span className="dv-info-title">What it does not do</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  <div className="dv-info-row--not"><i className="ph ph-x" /><span>No score, rating, or recommendation on the applicant.</span></div>
                  <div className="dv-info-row--not"><i className="ph ph-x" /><span>No credit check and no background check.</span></div>
                  <div className="dv-info-row--not"><i className="ph ph-x" /><span>No decision. You read the facts and decide.</span></div>
                </div>
              </div>
              <div className="dv-vault-card">
                <div className="dv-vault-card__title"><i className="ph ph-lock-simple" />Your tenant's documents</div>
                <p className="dv-vault-card__body">Stored encrypted in your document vault, visible only to you, and removable at any time.</p>
              </div>
            </div>
          </div>
        )}

        {screen === 2 && (
          <div className="dv-fade" style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            <div className="dv-upload-head">
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <h1 className="dv-h1" style={{ fontSize: 30 }}>Upload documents</h1>
                <p style={{ margin: 0, fontSize: 14.5, color: "var(--dv-gray-600)", maxWidth: "76ch", lineHeight: 1.6 }}>Each slot is pre-tagged, so every file is filed under the right document type. PDF and image files up to 25 MB.</p>
              </div>
              <div className="dv-province-chip">
                <i className="ph ph-map-pin" style={{ color: "var(--dv-orange-500)" }} />
                <span>Property province: <strong style={{ color: "var(--dv-ink)" }}>{province || "Not set"}</strong></span>
              </div>
            </div>

            <div className="dv-grid-2">
              <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
                {TIER_META.map((t, ti) => {
                  const slots = SLOTS.filter((sl) => sl.tier === ti);
                  return (
                    <div key={t.title} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div className="dv-tier-head">
                        <span className="dv-tier-title">{t.title}</span>
                        <span className="dv-tier-badge" style={{ background: t.badgeBg, color: t.badgeFg }}>{t.badge}</span>
                        <span className="dv-tier-note">{t.note}</span>
                      </div>
                      <div className="dv-slot-grid">
                        {slots.map((sl) => (
                          <SlotCard
                            key={sl.key}
                            slot={sl}
                            entries={files[sl.key] || []}
                            onAddFiles={handleAddFiles}
                            onRemoveFile={removeFile}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="dv-card dv-summary-card">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span className="dv-info-title">Ready to check</span>
                  <span style={{ fontSize: 12, color: "var(--dv-gray-500)" }}>{fileCount} files</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { ok: has("id"), label: "Photo ID" },
                    { ok: has("application"), label: "Rental application" },
                    { ok: has("income"), label: "At least one income document" },
                    { ok: has("bank"), label: "Bank statements (recommended)" },
                    { ok: has("credit"), label: "Tenant-provided credit report (recommended)" },
                  ].map((c) => (
                    <div className="dv-checklist-row" key={c.label}>
                      <i className={`ph ${c.ok ? "ph-check-circle" : "ph-circle-dashed"}`} style={{ color: c.ok ? "var(--dv-success-500)" : "var(--dv-gray-300)" }} />
                      <span style={{ color: c.ok ? "var(--dv-gray-700)" : "var(--dv-gray-500)" }}>{c.label}</span>
                    </div>
                  ))}
                </div>
                {requiredMet && recMissing.length > 0 && (
                  <div className="dv-nudge">For a more complete report, consider adding {recMissing.join(" and ")}. Not required — you can run the check without them.</div>
                )}
                {submitError && (
                  <div className="dv-nudge" style={{ borderColor: "var(--dv-warning-200)", color: "var(--dv-warning-700)" }}>
                    {submitError}
                  </div>
                )}
                <div className="dv-divider" />
                <div className="dv-consent-note">
                  <i className="ph ph-check-square-offset" />
                  <span>You confirmed the tenant provided these documents and gave permission to upload them.</span>
                </div>
                <button
                  type="button"
                  className={`dv-btn dv-btn--lg dv-btn--full${requiredMet ? " dv-btn--glow" : ""}`}
                  disabled={!requiredMet || isSubmitting}
                  onClick={() => requiredMet && !isSubmitting && runVerification()}
                >
                  {isSubmitting ? "Submitting documents..." : "Verify documents"}
                </button>
                <button type="button" className="dv-btn dv-btn--secondary dv-btn--full" onClick={goStart}>
                  Back
                </button>
              </div>
            </div>
          </div>
        )}

        {screen === 3 && (
          <div className="dv-processing-wrap dv-fade">
            <div className="dv-card dv-processing-card">
              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", textAlign: "center" }}>
                {uploadQueued ? (
                  <FeaturedIcon icon="ph-check-circle" size={56} />
                ) : (
                  <div className="dv-spinner" />
                )}
                <h2 style={{ margin: "14px 0 0", fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em" }}>
                  {uploadQueued ? "Documents uploaded" : "Uploading your documents"}
                </h2>
                <p style={{ margin: 0, fontSize: 13.5, color: "var(--dv-gray-500)", lineHeight: 1.6 }}>
                  {uploadQueued
                    ? "Your verification is now running in the background. It usually takes 10 to 15 minutes. You can leave this page and continue your work; we will notify you when the report is ready."
                    : "Your documents are being uploaded and organized. This should only take a moment."}
                </p>
              </div>
              {uploadQueued ? (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div className="dv-stage-row" style={{ background: "var(--dv-success-50)", borderColor: "var(--dv-success-500)" }}>
                      <i className="ph ph-check-circle" style={{ color: "var(--dv-success-500)" }} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
                        <span className="dv-stage-row__label" style={{ color: "var(--dv-ink)" }}>Upload complete</span>
                        <span className="dv-stage-row__sub">Files are saved securely and ready for AI verification.</span>
                      </div>
                      <span className="dv-stage-row__status" style={{ color: "var(--dv-success-500)" }}>Done</span>
                    </div>
                    <div className="dv-stage-row" style={{ background: "var(--dv-orange-50)", borderColor: "var(--dv-border-brand-soft)" }}>
                      <i className="ph ph-clock" style={{ color: "var(--dv-orange-500)" }} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
                        <span className="dv-stage-row__label" style={{ color: "var(--dv-ink)" }}>Verification queued</span>
                        <span className="dv-stage-row__sub">AI review will run in the background and save the result to Past verifications.</span>
                      </div>
                      <span className="dv-stage-row__status" style={{ color: "var(--dv-orange-500)" }}>Queued</span>
                    </div>
                  </div>
                  <div className="dv-caveat" style={{ alignItems: "flex-start" }}>
                    <i className="ph ph-bell" />
                    <span>You will receive an email/notification when this document verification is complete.</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <button
                      type="button"
                      className="dv-btn dv-btn--primary dv-btn--lg dv-btn--full"
                      onClick={() => navigate("/document-verification/past")}
                    >
                      <i className="ph ph-clock-counter-clockwise" />
                      View in Past verifications
                    </button>
                    <button type="button" className="dv-btn dv-btn--outline dv-btn--lg dv-btn--full" onClick={goStart}>
                      Start another verification
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {stages.map((g) => (
                      <div className="dv-stage-row" key={g.label} style={{ background: g.bg, borderColor: g.border }}>
                        <i className={`ph ${g.icon}`} style={{ color: g.iconColor }} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
                          <span className="dv-stage-row__label" style={{ color: g.textColor }}>{g.label}</span>
                          <span className="dv-stage-row__sub">{g.sub}</span>
                        </div>
                        <span className="dv-stage-row__status" style={{ color: g.iconColor }}>{g.status}</span>
                      </div>
                    ))}
                  </div>
                  <div className="dv-progress-track"><div className="dv-progress-bar" /></div>
                  <p style={{ margin: 0, textAlign: "center", fontSize: 12, color: "var(--dv-gray-400)", lineHeight: 1.6 }}>We are saving your uploaded documents. We are not scoring or rating the applicant.</p>
                </>
              )}
            </div>
          </div>
        )}

        {screen === 4 && (
          <div className="dv-fade" style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            <div className="dv-report-head">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <h1 className="dv-h1">Document Verification Summary</h1>
                <div className="dv-report-meta">
                  <span>
                    Generated {reportGenerated ? new Date(reportGenerated).toLocaleDateString() : "after verification"}
                  </span>
                  <span className="dv-dot" />
                  <span>Property province: <strong style={{ color: "var(--dv-gray-700)" }}>{province || "Not set"}</strong></span>
                  <span className="dv-dot" />
                  <span>Applicant (label only): <strong style={{ color: "var(--dv-gray-700)" }}>{applicantLabel}</strong></span>
                </div>
                <p className="dv-report-disclaimer">A factual summary of the documents provided. This is not a recommendation, a credit report, or a guarantee. You make the final decision.</p>
              </div>
              <div className="dv-report-actions">
                <button type="button" className="dv-btn" onClick={() => window.print()}>
                  <i className="ph ph-download-simple" />
                  Download PDF
                </button>
                <button type="button" className="dv-btn dv-btn--outline" onClick={goStart}>
                  <i className="ph ph-arrow-counter-clockwise" />New verification
                </button>
              </div>
            </div>

            {(isLoadingExisting || submitError) && (
              <div
                className="dv-nudge"
                style={{
                  borderColor: submitError ? "var(--dv-warning-200)" : "var(--dv-gray-200)",
                  color: submitError ? "var(--dv-warning-700)" : "var(--dv-gray-600)",
                }}
              >
                {isLoadingExisting ? "Loading verification report..." : submitError}
              </div>
            )}

            <div className="dv-card" style={{ overflow: "hidden" }}>
              <div className="dv-summary-panel-head">
                <i className="ph ph-list-magnifying-glass" />
                <span>What we checked and what we found</span>
                <span>A summary of facts — not a rating.</span>
              </div>
              <div className="dv-summary-cols">
                {reportSummaryCols.map((col) => (
                  <div className="dv-summary-col" key={col.title}>
                    <div className="dv-summary-col__head">
                      <i className={`ph ${col.icon}`} style={{ color: col.color }} />
                      <span className="dv-summary-col__title">{col.title}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                      {col.items.map((it) => (
                        <div className="dv-summary-col__item" key={it}>
                          <i className={`ph ${col.bullet}`} style={{ color: col.color }} />
                          <span>{it}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dv-card dv-reviewed-bar">
              <span className="dv-reviewed-bar__title">Documents reviewed</span>
              <div className="dv-reviewed-bar__chips">
                {dynamicReviewed.map((r) => (
                  <span className="dv-chip" key={r.label}><i className={`ph ${r.icon}`} />{r.label}</span>
                ))}
              </div>
              <span className="dv-reviewed-bar__note">The rental application is the anchor document — the other files are checked against it.</span>
            </div>

            <div className="dv-report-grid">
                {reportSections.map((sec) => (
                <div className="dv-card dv-section-card" key={sec.title} style={{ gridColumn: sec.span || "span 1" }}>
                  <div className="dv-section-head">
                    <FeaturedIcon icon={sec.icon} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span className="dv-section-title">{sec.title}</span>
                      <span className="dv-section-sub">{sec.sub}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {sec.rows.map((row) => (
                      <div className="dv-row" key={row.label}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                          <span className="dv-row__label">{row.label}</span>
                          {row.detail && <span className="dv-row__detail">{row.detail}</span>}
                        </div>
                        {row.value && <span className="dv-row__value">{row.value}</span>}
                        {!row.noBadge && row.badgeColor && (
                          <Badge color={row.badgeColor} icon={row.badgeIcon}>{row.status}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                  {sec.caveat && (
                    <div className="dv-caveat">
                      <i className="ph ph-info" />
                      <span>{sec.caveat}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="dv-disclaimer-card">
              <i className="ph ph-scales" />
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span className="dv-disclaimer-card__title">Please read this before you decide</span>
                <p className="dv-disclaimer-card__body">
                  {currentAiResult?.disclaimer ||
                    "This summary is based only on the documents you uploaded. We check for signs of tampering and for consistency across documents, but we cannot guarantee any document is authentic, and a well-made forgery may pass these checks. This is not a credit report, a background check, or a recommendation, and it does not predict whether the tenant will pay. Verify anything important independently, such as by contacting the employer or previous landlord, and make your own decision. HomeOwner does not decide whether to approve a tenant."}
                </p>
                <div className="dv-disclaimer-card__links">
                  <button type="button">How we check documents</button>
                  <span style={{ color: "var(--dv-gray-300)" }}>&middot;</span>
                  <button type="button">Your responsibilities as a landlord in {province || "your province"}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentVerification;
